import { useRouter } from "next/router";
import {
  Box,
  Center,
  Divider,
  Flex,
  Grid,
  GridItem,
  ListItem,
  OrderedList,
  Show,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { BsChevronRight } from "react-icons/bs";

// Constants
import { VaultType } from "constants/vaults";
import { CLICK_PORTFOLIO_TAB, KNOW_MORE_CTA } from "constants/analytics";

// Components
import Deposit from "./components/Deposit";
import Withdraw from "./components/Withdraw";

// Hooks
import useApp from "hooks/useApp";

// utils
import { getQueryString } from "utils/common";

const DeFiStrategy = ({
  vault,
  isTVLLimitReached,
  isWalletLimitReached,
}: {
  vault: any;
  isTVLLimitReached: boolean;
  isWalletLimitReached: boolean;
}) => {
  const router = useRouter();

  const { track } = useApp();

  const vaultDetails = vault.original as VaultType;
  const queryString = getQueryString(router.query);

  const disabledTxnMessage =
    isTVLLimitReached || isWalletLimitReached
      ? isTVLLimitReached
        ? "TVL cap hit, No more deposits allowed. Please check back soon for refreshed limit."
        : "Wallet cap hit, No more deposits allowed from the wallet. Please check back soon for refreshed limit."
      : "";

  return (
    <>
      <Divider pos="relative" top="-1rem" borderColor="brand.separator" />
      <Grid templateColumns={{ base: "1fr", lg: "1fr 0.6fr" }} pb="2rem">
        <GridItem>
          <Tabs
            isFitted
            variant="unstyled"
            p="1rem"
            borderRadius="0.5rem"
            border="1px solid"
            borderColor="brand.separator"
            onClick={(e) =>
              track(CLICK_PORTFOLIO_TAB, {
                tab: (e.target as any).innerText.toLowerCase(),
              })
            }
          >
            <TabList color="brand.textInactive">
              <Tab
                pl="0"
                outline="none"
                border="none"
                fontFamily="semi-bold"
                color="brand.textInactive"
                _focus={{
                  outline: "none",
                }}
                _selected={{
                  color: "brand.textColor",
                }}
              >
                <Text fontSize={{ base: 14, lg: 20 }}>Deposit</Text>
              </Tab>
              <Box
                height="28px"
                position="relative"
                top={{ base: "4px", lg: "12px" }}
              >
                <Divider orientation="vertical" borderColor="brand.separator" />
              </Box>
              <Tab
                pr="0"
                outline="none"
                border="none"
                fontFamily="semi-bold"
                color="brand.textInactive"
                _focus={{
                  outline: "none",
                }}
                _selected={{
                  color: "brand.textColor",
                }}
              >
                <Text fontSize={{ base: 14, lg: 20 }}>Withdraw</Text>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel mt="1rem" px="0" pb="0">
                <Deposit
                  vault={vaultDetails}
                  disabledTxnMessage={disabledTxnMessage}
                />
              </TabPanel>
              <TabPanel mt="1rem" px="0" pb="0">
                <Withdraw vault={vaultDetails} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>
        <GridItem>
          <Box px="1rem">
            <Show above="lg">
              <Text fontSize={14} fontFamily="semi-bold">
                Strategy
              </Text>
              <UnorderedList
                fontSize={14}
                mt="0.5rem"
                color="brand.textInactive"
                spacing="0.25rem"
                pl="0.5rem"
              >
                {vaultDetails.highlights.strategy.map((text) => (
                  <ListItem key={text}>{text}</ListItem>
                ))}
              </UnorderedList>
              <Text fontSize={14} mt="1.5rem" fontFamily="semi-bold">
                Withdrawal Details:
              </Text>
              <OrderedList
                fontSize={14}
                mt="0.5rem"
                color="brand.textInactive"
                spacing="0.25rem"
                pl="0.5rem"
              >
                {vaultDetails.highlights.withdraw.map((text) => (
                  <ListItem key={text}>{text}</ListItem>
                ))}
              </OrderedList>
              <Flex
                align="center"
                fontFamily="semi-bold"
                mt="1.5rem"
                gap="0.25rem"
                cursor="pointer"
                onClick={() =>
                  router.push(
                    `/portfolios/${vaultDetails.key}${
                      queryString ? `/?${queryString}` : ""
                    }`
                  )
                }
              >
                <Text>Know More</Text>
                <BsChevronRight fontSize={12} />
              </Flex>
            </Show>
            <Show below="lg">
              <Center
                fontFamily="semi-bold"
                mt="1.5rem"
                gap="0.25rem"
                fontSize={14}
                cursor="pointer"
                onClick={() => {
                  track(KNOW_MORE_CTA, {
                    portfolio: vaultDetails.name,
                  });
                  router.push(`/portfolios/${vaultDetails.key}`);
                }}
              >
                <Text>Know more about the strategy</Text>
                <BsChevronRight fontSize={12} />
              </Center>
            </Show>
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};

export default DeFiStrategy;
